<template>
  <div class="p-test-table-container">
    <!-- ヘッダー部分 -->
    <div class="p-test-table-header">
      <div class="p-test-table-info-group">
        <div>{{ academicSession?.name }}</div>
        <div>{{ academicYear?.name }}</div>
        <div>{{ classroom?.name }}</div>
        <div>{{ subjectField?.name }}</div>
        <div><plain-text-view v-if="cbtUnit" :markdownText="cbtUnit.title"/></div>
      </div>
    </div>
    <div v-if="showBlankPage" class="c-table-test-list-wrapper -answer -empty">
      <div class="c-empty-message">
        <div class="c-empty-message__icon"><img src="../../../../djangoapp/assets/teacher/img/common/icon_empty.svg" alt=""></div>
        <div class="c-empty-message__title">データがありません</div>
        <div class="c-empty-message__text">
          選択した条件で、テストが実施されていません。<br>
          実施状況をご確認ください。
        </div>
      </div>
    </div>
    <div v-else class="c-table-test-list-wrapper -answer">
      <!-- NFR-7362: 観点の凡例。集計画面(正答率凡例)と同じ「色ボックス＋テキスト」形式に合わせる -->
      <div class="p-test-criterion-legend">
        <div>観点</div>
        <div class="p-test-table-legend-item">
          <span class="p-test-table-color-box -criterion-chigi"></span>
          <span>知・技</span>
        </div>
        <div class="p-test-table-legend-item">
          <span class="p-test-table-color-box -criterion-shihanhyou"></span>
          <span>思・判・表</span>
        </div>
      </div>
      <!-- テーブル部分 -->
      <table class="p-test-table">
        <!-- テーブルヘッダー -->
        <thead>
          <tr>
            <!-- NFR-7362: 出席番号・名前は配点行まで縦結合する -->
            <th class="number" rowspan="2">出席番号</th>
            <th class="name" rowspan="2">名前</th>
            <!-- 解答列（1段目ヘッダー: 項番号。完答小問でも結合せず、各解答欄ごとに表示する） -->
            <th
              v-for="quiz in displayQuizzes"
              :key="quiz.quiz_id"
              class="test-question first"
            >
              {{ quiz.title }}
            </th>
          </tr>
          <!-- NFR-7362: 配点行。各セルの背景色を観点(criterion)で色分けする（凡例はテーブル上部に表示）。
               完答小問はこの配点行だけ1セルに結合し「完答：N点」を表示する（項番号行は結合しない）。 -->
          <tr class="p-test-table-point-row">
            <th
              v-for="cell in pointCells"
              :key="cell.key"
              class="test-question second"
              :class="{ 'p-test-kanto-cell': cell.merged }"
              :colspan="cell.colspan"
            >
              <span class="p-test-point-cell">
                <span
                  class="p-test-table-color-box"
                  :class="{
                    '-criterion-chigi': cell.criterion === CbtCriterion.CHIGI,
                    '-criterion-shihanhyou': cell.criterion === CbtCriterion.SHIHANHYOU,
                  }"
                ></span>{{ cell.merged ? `完答：${cell.pointValue}点` : `${cell.pointValue}点` }}
              </span>
            </th>
          </tr>
        </thead>

        <!-- テーブルボディ -->
        <tbody v-if="students?.length && answerResults">
          <tr
              v-for="student in studentsWithAnswers"
              :key="student.id"
              :class="{ gray: !student.answers.length }"
          >
            <td class="row-number u-text-ellipsis">{{ student.number }}</td>
            <td>
              <div class="row-name">
                <div class="u-text-ellipsis">{{ student.name }}</div>
              </div>
            </td>

            <template v-if="student.answers.length">
              <td
                  v-for="answerResult in student.answers"
                  :key="answerResult.cbt_quiz_id"
                  :class="['test-question', { '-undo': isRevertible(answerResult) }]"
              >
                <div
                    class="test-question-contents"
                    :class="{
                      correct: answerResult.is_correct_answer,
                      wrong: !answerResult.is_correct_answer,
                      'test-question-long': answerResult.answer.length >= 15,
                    }"
                >
                  <student-answer-view
                      :answer="answerResult.answer"
                      :answer-format="answerResult.answerFormat"
                      :subject-field="subjectFieldValue"
                  />
                </div>
                <!-- 正誤変更/正誤変更済リンクは「記述式」の解答でのみ表示（ただし無解答の場合は表示しない） -->
                <!-- FIXME NFR-7683:
                  ボタンの文字をコピー対象から除外する処理。
                  NFR-7683で必要に応じて data-copy-value に置き換えること。
                -->
                <template v-if="answerResult.answerFormat === QUIZTYPE.DESCRIPTION">
                  <button
                    v-if="!answerResult.is_correct_answer && answerResult.rawAnswer !== ''"
                    class="link"
                    data-copy-ignore
                    @click="openChangeToCorrectModal(answerResult.cbt_quiz_id, answerResult.rawAnswer, answerResult.answer, answerResult.answerFormat)"
                  >
                    正誤変更
                  </button>
                  <button
                    v-else-if="isRevertible(answerResult)"
                    class="link"
                    data-copy-ignore
                    @click="openRevertToIncorrectModal(answerResult.cbt_quiz_id, answerResult.rawAnswer, answerResult.answer, answerResult.answerFormat)"
                  >
                    正誤を元に戻す
                  </button>
                </template>
                <span
                    v-if="answerResult.has_departure"
                    class="c-parts-unanswered p-test-cell-leave-label"
                >画面離脱あり</span>
              </td>
            </template>
            <template v-else>
              <!-- NOTE: 未提出・未解答の生徒には、解答欄数分空tdをレンダリング -->
              <td
                  v-for="n in quizzesCount"
                  :key="n"
              ></td>
            </template>
          </tr>
        </tbody>

        <!-- テーブルフッター -->
        <tfoot>
          <tr class="p-test-table-footer">
            <th colspan="2">模範解答</th>
            <question-correct-answer
              v-for="quiz in displayQuizzes"
              :key="quiz.quiz_id"
              tag="th"
              :answer-format="quiz.answer_format"
              :choices-label="quiz.choices_label"
              :correct-answers="quiz.correct_answers"
            ></question-correct-answer>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
  <!-- 正誤変更モーダル -->
  <cbt-change-correctness-modal
    :is-open="isOpenChangeCorrectnessModal"
    :is-revert="isRevert"
    :answer="correctnessChangeStudentAnswer"
    :answer-format="correctnessChangeAnswerFormat"
    :subject-field="subjectFieldValue"
    @ok="handleChangeCorrectnessModalOk"
    @cancel="handleChangeCorrectnessModalCancel"
  ></cbt-change-correctness-modal>
  <!-- 正誤変更処理中モーダル（モーダルの文言はデフォルトのまま） -->
  <in-progress-modal
    :is-open="isOpenChangeCorrectnessInProgressModal"
  ></in-progress-modal>
  <error-modal></error-modal>
</template>

<script setup lang="ts">
  import { onMounted, ref, computed } from "vue";
  import { useCbtStore } from "@/store/teacher/cbt_store";
  import { useClassroomStore } from "@/store/teacher/classroom_store";
  import { SUBJECTFIELD_MAP } from "@/consts/subject";
  import { QUIZTYPE } from "@/consts/answer_column";
  import { CbtCriterion } from "@/consts/cbt";
  import {
    ISelectSingleOption,
    ICbtAnswerResults,
    ICbtCorrectAnswers,
    ICbtQuizStudentAnswer,
    ICbtSubjectFieldSelectOption,
    ICbtUnit,
    ICbtWrittenRandomOrderStudentAnswer,
    IStudentBrief,
  } from "@/consts/interfaces";

  /** 生徒の解答結果 */
  interface ICbtQuizStudentAnswerWithAnswerFormat extends Omit<ICbtQuizStudentAnswer, 'answer'> {
    answer: string;  // 生徒の解答 (normalizeAnswerでstringに正規化済み)
    rawAnswer: ICbtQuizStudentAnswer['answer'];  // 生徒の解答 (DB保存値そのまま)
    answerFormat: number;
  }

  /** 解答結果一覧を持つ生徒情報 */
  interface IStudentWithAnswers extends IStudentBrief {
    answers: ICbtQuizStudentAnswerWithAnswerFormat[];
  }

  const cbtStore = useCbtStore();
  const classroomStore = useClassroomStore()

  // 画面上部に表示される各検索条件
  // FIXME: 学級・項目は、CBT Ver1.0 以降で、セレクトボックス選択による検索に対応するときは、
  // CbtAnswer.vue の実装に倣って、selectedClassroom, selectedCbtUnit のような変数名に変更したほうが良いかも
  const academicSession = ref<ISelectSingleOption | null>(null);
  const academicYear = ref<ISelectSingleOption | null>(null);
  const classroom = ref<ISelectSingleOption | null>(null);
  const subjectField = ref<ICbtSubjectFieldSelectOption | null>(null);
  const cbtUnit = ref<ICbtUnit | null>(null);
  // 生徒一覧
  const students = ref<IStudentBrief[] | null>(null);
  // 項番号と模範解答(模範解答はVer1.0対象外)
  const correctAnswers = ref<ICbtCorrectAnswers | null>(null);
  // 解答結果一覧
  const answerResults = ref<ICbtAnswerResults | null>(null);
  // ブランクページの表示条件に該当するかどうか
  // * 学校に有効なCBT教材のライセンスが存在しないとき
  //   * ブランクページは、本来はデータがないときに表示するためのものだが、このパターンでも流用して表示する
  //   * cf. https://meiji01.backlog.com/view/NFR-6841
  // * 学級でCBT未実施の「項目」が選択されたとき
  // * 学級でCBT実施済だが提出者がいない「項目」が選択されたとき
  const showBlankPage = ref<boolean>(false);
  // 正誤変更モーダルを開くかどうか
  const isOpenChangeCorrectnessModal = ref<boolean>(false);
  // 正誤変更モーダルが「正答→誤答」方向かどうか（false = 誤答→正答）
  const isRevert = ref<boolean>(false);
  // 正誤変更対象データ
  const correctnessChangeQuizId = ref<number | null>(null);
  const correctnessChangeRawAnswer = ref<ICbtQuizStudentAnswer['answer'] | null>(null);
  const correctnessChangeStudentAnswer = ref<string>('');
  const correctnessChangeAnswerFormat = ref<number>(0);
  // 正誤変更処理中モーダルを開くかどうか
  const isOpenChangeCorrectnessInProgressModal = ref<boolean>(false);

  // 表示対象の解答欄（全解答欄。項番号行・生徒セル・模範解答は結合も非表示もせず全列を表示する）
  const displayQuizzes = computed(() => correctAnswers.value?.cbt_quizzes ?? []);

  // 表示する解答欄数を返す（未提出生徒の空セル用）
  const quizzesCount = computed(() => displayQuizzes.value.length);

  /** 配点行(2段目)のセル描画単位 */
  interface IPointCell {
    merged: boolean;      // 完答小問として結合するか
    colspan: number;      // 結合する解答欄数
    key: string | number; // v-for キー
    pointValue: number;   // 配点（結合時は完答小問の配点）
    criterion: number;    // 観点（配点セルの色分け用）
  }

  // NFR-7362: 配点行(2段目)だけを結合する。項番号行(1段目)は結合も非表示もしない。
  // 結合ルール:
  //   - 完答フラグ(all_correct_required)が付いている小問は、同一小問(cbt_question_id)の
  //     連続する解答欄を必ず1セルに結合し、「完答：N点」を表示する
  //     （Nはその小問の配点0以外の解答欄＝配点記録用の欄の値）
  //   - 完答フラグが付いていない小問は結合せず、解答欄ごとに個別の配点セルを出す
  const pointCells = computed<IPointCell[]>(() => {
    const quizzes = displayQuizzes.value;
    const cells: IPointCell[] = [];
    let i = 0;
    while (i < quizzes.length) {
      const quiz = quizzes[i];
      if (quiz.all_correct_required) {
        // 完答小問: 同一小問の連続する解答欄を必ず1セルに結合する
        let j = i;
        while (j < quizzes.length && quizzes[j].cbt_question_id === quiz.cbt_question_id) {
          j++;
        }
        const group = quizzes.slice(i, j);
        const nonZeroQuiz = group.find(q => q.point_value !== 0);
        // 配点を持つ解答欄（配点記録用に自動生成される欄）は観点が「知識・技能」固定で
        // 作られるため（cbt_work/repositories.py create_dummy_quiz）、小問本来の観点と
        // ずれることがある。観点は本体解答欄（配点0側）から取得する
        const criterionQuiz = group.find(q => q.point_value === 0) ?? group[0];
        cells.push({
          merged: true,
          colspan: group.length,
          key: `kanto-${quiz.cbt_question_id}`,
          pointValue: nonZeroQuiz?.point_value ?? 0,
          criterion: criterionQuiz.criterion,
        });
        i = j;
      } else {
        cells.push({
          merged: false,
          colspan: 1,
          key: quiz.quiz_id,
          pointValue: quiz.point_value,
          criterion: quiz.criterion,
        });
        i++;
      }
    }
    return cells;
  });

  // 教科分野の識別子（"rika" 形式）を返す
  const subjectFieldValue = computed(() => {
    return subjectField.value?.name ? SUBJECTFIELD_MAP[subjectField.value.name] : undefined;
  });

  /**
   * 順不同記述式の解答群を、「｜」区切りの文字列に変換する
   *
   * ワークとCBTのデータフォーマットの差異をここで吸収する。
   * 以降の処理では、ワーク・CBTともに同じように取り扱えるようにするため。
   */
  const normalizeAnswer = (answer: ICbtQuizStudentAnswer['answer'], answerFormat: number): string => {
    if (answerFormat === QUIZTYPE.WRITTEN_RANDOM_ORDER) {
      return (answer as ICbtWrittenRandomOrderStudentAnswer[]).map(answerItem => answerItem.user_answer).join("｜");
    }
    return answer as string;
  };

  /**
   * 各生徒にその生徒の解答結果一覧を紐づけた配列を返す
   *
   * テンプレート内で生徒の解答結果を効率良く参照できるようにするため、
   * 事前に生徒一覧と解答結果一覧を組み合わせておく
   *
   * @returns 各生徒にその生徒の answers プロパティ (解答結果一覧) を追加した配列。APIレスポンス未取得時は空配列。
   */
  const studentsWithAnswers = computed<IStudentWithAnswers[]>(() => {
    // API レスポンスがまだ返ってきていない場合は空配列を返す
    if (!correctAnswers.value || !students.value || !answerResults.value) return [];

    // quiz_id から answer_format へのマップを作成
    const quizFormatMap: Record<number, number> = {};
    for (const quiz of correctAnswers.value.cbt_quizzes) {
      quizFormatMap[quiz.quiz_id] = quiz.answer_format;
    }

    // answerResults から生徒IDをキーにしたマップを作成
    const studentAnswersMap: Record<number, ICbtQuizStudentAnswer[]> = {};
    for (const studentResult of answerResults.value.student_answers) {
      studentAnswersMap[studentResult.student_id] = studentResult.answers;
    }

    return students.value.map(student => ({
      ...student,
      answers: (studentAnswersMap[student.id] ?? []).map(answer => {
        const answerFormat = quizFormatMap[answer.cbt_quiz_id];
        if (answerFormat === undefined) {
          console.error(`Unknown cbt_quiz_id: ${answer.cbt_quiz_id}`);
        }
        return {
          ...answer,
          answer: normalizeAnswer(answer.answer, answerFormat),
          rawAnswer: answer.answer,  // DB保存値そのままの生徒の解答
          answerFormat: answerFormat
        };
      })
    }));
  });

  // 初期表示時
  onMounted(async() => {
    const params = getInitialQueryParams();

    // パラメータが不足している場合は、API呼び出しをスキップ
    if (!Object.values(params).every(v => v !== null)) {
      console.error('Required query parameters are missing:', params);
      return;
    }

    // 画面上部に表示するために、クエリパラメーターから「年度」「学年」「学級」「教科」「項目」を取得
    // NOTE: fetchCbtYearClass() で、
    // エラー、かつ、404 (有効なCBT教材のライセンスがありません) の場合、
    // ブランクページを表示する。以降の処理も不要なのでここで終了。

    const cbtYearClassResponse = await cbtStore.fetchCbtYearClass();
    if ("E005" === cbtYearClassResponse?.code) {
      showBlankPage.value = true;
      return;
    }
    const cbtSubjectFieldsResponse = await cbtStore.fetchCbtSubjectFields(
      {
        academic_session_id: Number(params.academic_session_id),
        academic_year: Number(params.academic_year),
      }
    );
    const cbtUnitsResponse = await cbtStore.fetchCbtUnits(
      {
        academic_session_id: Number(params.academic_session_id),
        academic_year: Number(params.academic_year),
        subject_field_id: Number(params.subject_field_id),
      }
    );
    academicSession.value = cbtYearClassResponse?.academic_sessions.find(x => x.value === Number(params.academic_session_id)) ?? null;
    academicYear.value = cbtYearClassResponse?.academic_years[Number(params.academic_session_id)]?.find(x => x.value === Number(params.academic_year)) ?? null;
    classroom.value = cbtYearClassResponse?.classrooms[Number(params.academic_session_id)]?.[Number(params.academic_year)]?.find(x => x.value === params.classroom) ?? null;
    subjectField.value = cbtSubjectFieldsResponse?.find(x => x.id === Number(params.subject_field_id)) ?? null;
    cbtUnit.value = cbtUnitsResponse?.cbt_units.find(x => x.id === Number(params.cbt_unit_id)) ?? null;

    // 項番号取得
    correctAnswers.value = await cbtStore.fetchCbtCorrectAnswers({
      cbt_unit_id: Number(params.cbt_unit_id),
      classroom_uuid: params.classroom,
    });

    // 生徒一覧取得
    const studentListResponse = await classroomStore.fetchStudentList({classroom_uuid: params.classroom});
    students.value = studentListResponse?.students ?? null;

    // 解答結果一覧取得（生徒がいる場合のみ）
    if (students.value?.length) {
      const answerResultsResponse = await cbtStore.fetchCbtAnswerResults({
        cbt_unit_id: Number(params.cbt_unit_id),
        classroom_uuid: params.classroom
      });

      // ブランクページの表示条件に該当する場合は、以降の処理も不要なのでここで終了
      // * 学級でCBT未実施の「項目」が選択されたとき
      // * 学級でCBT実施済だが提出者がいない「項目」が選択されたとき
      if (
        answerResultsResponse?.text === "cbt_execution_not_found" ||
        answerResultsResponse?.student_answers.every(s => s.answers.length === 0)
      ) {
        showBlankPage.value = true;
        return;
      }

      answerResults.value = answerResultsResponse ?? null;
    }
  })

  // クエリパラメーターを正規化するユーティリティ関数
  const normalizeQueryParam = (x: string | null): string | null => x && x.trim() !== "" ? x : null;

  // 解答確認・修正の検索画面から渡されたクエリパラメーターを取得する
  function getInitialQueryParams() {
    const params = new URLSearchParams(window.location.search);
    return {
      academic_session_id: normalizeQueryParam(params.get("academic_session_id")),
      academic_year: normalizeQueryParam(params.get("academic_year")),
      classroom: normalizeQueryParam(params.get("classroom")),
      subject_field_id: normalizeQueryParam(params.get("subject_field_id")),
      cbt_unit_id: normalizeQueryParam(params.get("cbt_unit_id")),
    }
  }

  // 正誤変更
  async function changeToCorrectAnswer(
    cbtQuizId: number,
    rawAnswer: ICbtQuizStudentAnswer['answer']
  ) {
    // onMounted内のAPI呼び出しが失敗した場合を考慮
    if (!cbtUnit.value || !academicSession.value || !academicYear.value) {
      console.error('Required data is missing for correct answer change');
      return;
    }

    // rawAnswer を文字列に変換（JSONFieldの値をCharFieldとして保存するため）
    const answerString = typeof rawAnswer === 'string' ? rawAnswer : JSON.stringify(rawAnswer);

    openChangeCorrectnessInProgressModal();

    const result = await cbtStore.postCbtCorrectAnswerChange({
      cbt_quiz_id: cbtQuizId,
      academic_session_id: Number(academicSession.value.value),
      academic_year: Number(academicYear.value.value),
      answer: answerString,
    });

    // 処理が成功したら、解答一覧を再取得して表示を更新
    if (result) {
      const params = getInitialQueryParams();
      const answerResultsResponse = await cbtStore.fetchCbtAnswerResults({
        cbt_unit_id: Number(params.cbt_unit_id),
        classroom_uuid: params.classroom
      });
      answerResults.value = answerResultsResponse ?? null;
    }

    closeChangeCorrectnessInProgressModal();
  }

  function openChangeCorrectnessInProgressModal() {
    isOpenChangeCorrectnessInProgressModal.value = true;
  }

  function closeChangeCorrectnessInProgressModal() {
    isOpenChangeCorrectnessInProgressModal.value = false;
  }

  function isRevertible(answerResult: ICbtQuizStudentAnswerWithAnswerFormat): boolean {
    return answerResult.is_correct_answer && answerResult.is_correct_answer_changed;
  }

  function openChangeToCorrectModal(
    cbtQuizId: number,
    rawAnswer: ICbtQuizStudentAnswer['answer'],
    normalizedAnswer: string,
    answerFormat: number,
  ) {
    correctnessChangeQuizId.value = cbtQuizId;
    correctnessChangeRawAnswer.value = rawAnswer;
    correctnessChangeStudentAnswer.value = normalizedAnswer;
    correctnessChangeAnswerFormat.value = answerFormat;
    isRevert.value = false;
    isOpenChangeCorrectnessModal.value = true;
  }

  function openRevertToIncorrectModal(
    cbtQuizId: number,
    rawAnswer: ICbtQuizStudentAnswer['answer'],
    normalizedAnswer: string,
    answerFormat: number,
  ) {
    correctnessChangeQuizId.value = cbtQuizId;
    correctnessChangeRawAnswer.value = rawAnswer;
    correctnessChangeStudentAnswer.value = normalizedAnswer;
    correctnessChangeAnswerFormat.value = answerFormat;
    isRevert.value = true;
    isOpenChangeCorrectnessModal.value = true;
  }

  function closeChangeCorrectnessModal() {
    isOpenChangeCorrectnessModal.value = false;
    isRevert.value = false;
    correctnessChangeQuizId.value = null;
    correctnessChangeRawAnswer.value = null;
    correctnessChangeStudentAnswer.value = '';
    correctnessChangeAnswerFormat.value = 0;
  }

  async function handleChangeCorrectnessModalOk() {
    if (correctnessChangeQuizId.value === null || correctnessChangeRawAnswer.value === null) return;
    const quizId = correctnessChangeQuizId.value;
    const rawAnswer = correctnessChangeRawAnswer.value;
    const shouldRevert = isRevert.value;
    closeChangeCorrectnessModal();
    if (shouldRevert) {
      await revertToIncorrectAnswer(quizId, rawAnswer);
    } else {
      await changeToCorrectAnswer(quizId, rawAnswer);
    }
  }

  function handleChangeCorrectnessModalCancel() {
    closeChangeCorrectnessModal();
  }

  // 正誤を元に戻す (正誤変更済みの正答を元の誤答へ戻す)
  async function revertToIncorrectAnswer(
    cbtQuizId: number,
    rawAnswer: ICbtQuizStudentAnswer['answer']
  ) {
    // onMounted内のAPI呼び出しが失敗した場合を考慮
    if (!cbtUnit.value || !academicSession.value || !academicYear.value) {
      console.error('Required data is missing for correct answer change delete');
      return;
    }

    // rawAnswer を文字列に変換（JSONFieldの値をCharFieldとして保存するため）
    const answerString = typeof rawAnswer === 'string' ? rawAnswer : JSON.stringify(rawAnswer);

    openChangeCorrectnessInProgressModal();

    const result = await cbtStore.postCbtCorrectAnswerChangeDelete({
      cbt_quiz_id: cbtQuizId,
      academic_session_id: Number(academicSession.value.value),
      academic_year: Number(academicYear.value.value),
      answer: answerString,
    });

    // 処理が成功したら、解答一覧を再取得して表示を更新
    if (result) {
      const params = getInitialQueryParams();
      const answerResultsResponse = await cbtStore.fetchCbtAnswerResults({
        cbt_unit_id: Number(params.cbt_unit_id),
        classroom_uuid: params.classroom
      });
      answerResults.value = answerResultsResponse ?? null;
    }

    closeChangeCorrectnessInProgressModal();
  }
</script>

<style lang="scss" scoped>
/* NFR-7362: ヘッダー2行をスクロール時に確実に固定する。
   -answer では本文セル(td.test-question)が position:relative のため、
   z-index/背景を明示しないと後続の td がヘッダーの前面に描画されてしまう。
   継承(.first/.second)頼みをやめ、両行に sticky・top・z-index を明示する。 */
.p-test-table thead th {
  position: sticky;
  background-color: #f0f0f0;
  z-index: 3;
}
/* 1段目: 出席番号・名前・大問タイトル */
.p-test-table thead tr:first-child th {
  top: 0;
  height: 40px;
}
/* 2段目: 配点行（1段目の高さ40pxの直下に固定） */
.p-test-table-point-row th {
  top: 40px;
}
/* フッター（模範解答）も本文セルより前面に固定する */
.p-test-table tfoot th {
  z-index: 3;
}
</style>
